import type { PitchTrackerConfig } from './pitch-tracker';

/**
 * pitch-tracker.config.ts — 프로덕션 PitchTracker 설정의 **정본**.
 *
 * 예전엔 이 값들이 `pitch-tracker.mock.ts`에 살았다. mock 파일이 프로덕션 config의
 * 출처면 두 가지가 조용히 어긋난다: (1) mock 편의값(-60 같은)이 실기기까지 따라가고,
 * (2) mock만 구현한 노브(`eventIntervalMs`)가 전 경로에서 켜지는 것처럼 보인다.
 * 그래서 config는 여기로 나오고, mock 모듈은 그것을 **소비**한다.
 *
 * 값은 네이티브 브리지의 정본 분석 프로파일과 **같아야 한다**
 * (`apps/mobile/ios/Muzly/MuzlyPitchBridge.mm`,
 *  `apps/mobile/android/app/src/main/cpp/pitch/MuzlyPitchBridge.cpp` 의 `k*` 상수).
 * C/ObjC는 TS를 import할 수 없어 값이 양쪽에 존재한다 — 한쪽을 바꾸면 다른 쪽도 바꾼다.
 * (드리프트를 잡는 테스트는 `pitch-tracker.config.spec.ts`가 이 파일 쪽만 고정한다.)
 */

/** 캡처 샘플레이트(Hz) — 네이티브는 하드웨어 실제 레이트로 시작하고 이 값을 요청으로만 쓴다. */
const CAPTURE_SAMPLE_RATE = 48000;
/** 분석 창 / 홉 (스펙: 2048win / 1024hop @48k → 프레임 간격 ≈21.3ms). */
const WINDOW_SIZE = 2048;
const HOP_SIZE = 1024;

/**
 * 유성 판정의 confidence 하한. **이 상수가 유일한 출처다** — 엔진 게이트(config)와
 * 화면·스코어링의 프레임 필터가 같은 선을 써야 "엔진은 유성이라는데 그래프는 끊긴다"가
 * 안 생긴다. 네이티브 브리지의 `kConfidenceThreshold`가 같은 값을 들고 있다(위 주석).
 */
export const PITCH_CONFIDENCE_MIN = 0.5;

/**
 * 무음 게이트(dBFS). **판별자가 아니다** — 진짜 디지털 무음만 걷어내는 바닥이다.
 * 유·무성을 가르는 일은 `confidenceThreshold`가 한다.
 *
 * **-45 (2026-08-07).** 이력이 -60 → -45 → -35 → -60 → -45다.
 *
 * -60을 고른 근거가 된 실기기 계측은 지금도 유효하다
 * (`.claude/20260805/DEBUG-13-23-23.md`):
 *
 * | | rms(dBFS) | confidence |
 * |---|---|---|
 * | 발성 | -42 ~ -30 | 0.59 ~ 0.70 |
 * | 무발성 | -47 ~ -41.5 | **0.00** |
 *
 * **두 모집단이 dB에서 겹친다**(발성 -42 vs 무발성 -41.5) — 어떤 dB 선도 둘을 완전히
 * 가르지 못한다. -35는 실제 발성 구간의 **한가운데**여서 프레임의 절반을 죽였고, 그게
 * "그래프가 뜨문뜨문"의 원인이었다. 그건 여전히 맞다.
 *
 * 틀렸던 것은 **그 다음 전제**다. -60은 "이 바닥이 흘려보낸 것은 아래쪽의 상대 게이트가
 * 잡는다"를 깔고 있었는데(`scoring/voice-gate.ts`), 그 게이트는 온보딩 캡처 경로에서만
 * 돈다. 디버그 콘솔은 프레임을 엔진에서 바로 읽으므로 -60에서 **방을 그렸다**. 아무도
 * 소리를 안 내는 방 녹음을 프로덕션 엔진에 직접 물려 잰 값:
 *
 * | 바닥 | 유성 프레임 |
 * |---|---|
 * | -60 | **277 / 280** (midi 45.8~47.1 = A2~B2, confidence ~0.59) |
 * | -45 | **0 / 280** |
 *
 * 그래서 -45다. 발성 구간의 가장 조용한 3dB(-45~-42)를 내주는 대신, -60이 흘려보내던
 * 것을 되찾는다. 이 값은 여전히 유·무성 판별자가 아니다 — 그 일은 `confidenceThreshold`가 한다.
 *
 * **이 표를 "-45면 방이 안 걸린다"로 읽지 말 것.** 위 방 녹음은 -47.3dBFS로 합성한 것이고,
 * 그것은 실기기 계측 암소음 범위(-44.6 ~ -47)의 **조용한 끝**이다. 시끄러운 끝(-44.6)으로
 * 만들면 -45에서도 방 프레임 374개가 374개 전부 통과한다. 어떤 절대 dBFS도 방을 못 막는
 * 이유와 실측 표는 `scoring/voice-gate.ts` 머리말에 있고, 그것을 막는 것은 이 상수가
 * 아니라 거기 있는 **방 대비 게이트**다 — 프레임을 그리거나 채점하는 소비처는 그 게이트를
 * 태워야 한다(`useVoiceCapture`, `RealtimePitchGraph`).
 *
 * "노래는 -30~-15dBFS"라던 옛 근거는 이 캡처 경로(iOS `AVAudioSessionModeMeasurement`,
 * AGC 없음)에서 약 10dB 어긋난다. 처리가 켜진 마이크 기준 수치였다.
 *
 * 기기·방마다 다시 잡아야 하면 이 상수가 아니라 디버그 콘솔 튜닝 탭에서 잡는다.
 * 네이티브 사본 2곳(iOS/Android 브리지 `kSilenceThresholdDb`)이 같이 움직여야 한다.
 */
const SILENCE_THRESHOLD_DB = -45;

/** 분석 프레임 하나의 시간 간격(ms) — 홉을 캡처 레이트로 나눈 값. */
const analysisFrameMs = (hopSize: number, sampleRate: number): number =>
  (hopSize / sampleRate) * 1000;

/**
 * 이 config에서 프레임이 실제로 나오는 간격(ms). `eventIntervalMs`가 없으면 분석
 * 프레임 간격으로 떨어진다 — 네이티브가 스로틀 없이 프레임마다 방출하는 동작과 같다.
 * (예전 폴백은 `?? 50`이었는데, 그건 어디서도 근거가 없는 숫자였다.)
 */
export const effectiveEventIntervalMs = (config: PitchTrackerConfig): number =>
  config.output.eventIntervalMs ??
  analysisFrameMs(config.analysis.hopSize, config.audio.captureSampleRate);

/**
 * 프로덕션 config. `overrides`는 **최상위 섹션 단위**로 덮는다(얕은 병합) — 예컨대
 * `analysis` 하나만 넘기면 그 블록 전체가 교체되므로 부분 변경은 스프레드로 직접.
 */
export const createPitchTrackerConfig = (
  overrides: Partial<PitchTrackerConfig> = {},
): PitchTrackerConfig => ({
  version: 1,
  audio: { captureSampleRate: CAPTURE_SAMPLE_RATE, channelCount: 1 },
  analysis: {
    algorithm: 'mpm',
    sampleRateStrategy: 'native',
    // C2. 한때 75(D#2)로 올렸다가 실물 피아노 녹음으로 재고 되돌렸다: 바닥을 올리면
    // 저역에 고착된 프레임이 제 음이 되는 게 아니라 **무성이 된다** — 고쳐진 것보다
    // 입 막힌 게 많았고(Beethoven 15:17, Chopin 녹턴 424:505), 한 음씩 치는 계약 안
    // 입력에는 애초에 아무 차이도 없었다. 화음 공통주기 고착은 엔진에서 잡는다
    // (mpm_detector의 sub-period preference). 저역 럼블은 무음 게이트가 잡는다.
    minFrequencyHz: 65,
    maxFrequencyHz: 1100,
    windowSize: WINDOW_SIZE,
    hopSize: HOP_SIZE,
    silenceThresholdDb: SILENCE_THRESHOLD_DB,
    confidenceThreshold: PITCH_CONFIDENCE_MIN,
    // 표시용 스무딩은 기본 on: 네이티브 post-processor의 짧은 median이 단일 프레임
    // 옥타브 글리치를 걷어내므로 UI가 다시 할 필요가 없다. 스코어링은 raw f0 그대로.
    smoothing: 'light',
  },
  output: {
    // **네이티브는 이 값을 스로틀로 쓰지 않는다** — 분석 프레임마다 하나씩 방출한다.
    // 그래서 여기 적히는 값은 "요청"이 아니라 그 실제 간격(≈21.3ms)이다. 예전의
    // `50`은 mock만 지키던 숫자여서, 실기기에서는 광고와 실제가 2배 넘게 달랐다.
    // (기기 이벤트 주기를 정말 낮추려면 네이티브에 스로틀을 구현해야 한다.)
    eventIntervalMs: analysisFrameMs(HOP_SIZE, CAPTURE_SAMPLE_RATE),
    includePerformanceMetrics: false,
  },
  ...overrides,
});
